<script setup lang="ts">
import { computed } from 'vue'

import QuotaChips from '@/components/QuotaChips.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import type { License, LicenseAddon, ProductSpec } from '@/api/ls'
import {
  addonSourceLabel,
  addonStatusLabel,
  addonSummary,
  moduleAddonHint,
} from '@/lib/licenseLabels'
import { effectiveStatus, fmtModulesZh, statusLabel } from '@/lib/modules'
import { planLabel } from '@/lib/quotas'
import { formatRemaining } from '@/lib/terms'
import { vCardTable } from '@/lib/cardTable'

/**
 * 授權詳情（批110）——「這張證到底買了什麼」的單一入口。
 *
 * ## 為什麼有這個 modal
 *
 * 授權清單原本是 9 欄，模組 chips、套裝＋配額 chips、子證展開列全部攤在表格裡，min-content
 * 需求超過 1500px——1366 筆電扣掉側欄與 padding 只剩約 1023px，於是表格撐破卡片、資料列
 * 跑到白底外面。批110 把清單收成 6 欄，這三塊內容移進這裡。
 *
 * ## 這是**純顯示**元件
 *
 * 所有操作（換發、子證的下載／複製／續期／撤銷）一律 `emit` 給父層——那些函式本來就在
 * `LicensesView` 裡（帶著 dialogs 確認框、API 呼叫與清單重載），搬過來只會變成兩份。
 * 代理商 realm 共用同一個元件，差別是父層給的 `can-*` 旗標與 `addons` 來源
 * （我方開啟時才打端點、代理商直接從清單投影拿）。
 */
const props = defineProps<{
  open: boolean
  license: License | null
  /** 該產品的憑證設計（模組／配額的中文名、行為參數的宣告）。 */
  spec: ProductSpec | null
  addons: LicenseAddon[]
  addonsBusy?: boolean
  /** 顯示子證的續期／撤銷（代理商 realm 沒有這兩顆）。 */
  canManageAddons?: boolean
  /** 顯示「換發新證」（只對 active 的證有意義）。 */
  canReissue?: boolean
}>()
const emit = defineEmits<{
  close: []
  reissue: []
  'addon-save': [addon: LicenseAddon]
  'addon-copy': [addon: LicenseAddon]
  'addon-renew': [addon: LicenseAddon]
  'addon-revoke': [addon: LicenseAddon]
}>()

/** 模組／配額一律取 **effective**（主證＋生效加購）——與清單摘要同一個來源。 */
const modules = computed(() =>
  props.license
    ? fmtModulesZh(props.spec, props.license.effective_modules ?? props.license.modules)
    : [],
)
/** 配額的**有效值**（主證＋生效加購）；`undefined` 是舊回應沒有這個欄位的相容退路。 */
const effectiveQuotas = computed(() =>
  props.license?.effective_quotas === undefined
    ? (props.license?.quotas ?? null)
    : props.license.effective_quotas,
)

/** 行為參數（批110）：只列這個產品宣告過的鍵，值缺席就顯示 `absent_default`。 */
const policyRows = computed(() => {
  const declared = props.spec?.policies ?? []
  if (!declared.length) return []
  const values = props.license?.policy ?? {}
  return declared
    .filter((d) => !d.deprecated || values[d.key] !== undefined)
    .map((d) => {
      const raw = values[d.key]
      const has = raw !== undefined && raw !== null
      return {
        key: d.key,
        label: d.label,
        text: has ? String(raw) : `${d.absent_default ?? '—'}（預設）`,
        custom: has,
      }
    })
})

function addonOf(kind: 'modules' | 'quotas', key: string): boolean {
  return props.license?.addon_sources?.[kind]?.[key] !== undefined
}

function fmt(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : '尚未起算'
}
</script>

<template>
  <UiModal :open="open" title="授權內容" width="720px" @close="emit('close')">
    <template v-if="license">
      <div class="head">
        <div>
          <!-- ★ 與清單同一支 helper：`status` 是 DB 值（active|revoked|superseded），
               「已到期」是**推導**出來的，直接印原值會讓一張過期的證顯示「生效中」。 -->
          <span class="status-pill" :class="effectiveStatus(license)">
            {{ statusLabel(effectiveStatus(license)) }}
          </span>
          <strong class="plan">{{ planLabel(spec, license.plan) }}</strong>
          <code class="mono" :title="license.license_id">{{ license.license_id.slice(0, 8) }}</code>
        </div>
        <div class="term">
          <span v-if="license.not_before" class="chip pending">
            {{ fmt(license.not_before) }} 起生效
          </span>
          <span>到期 {{ fmt(license.expires_at) }}</span>
          <small v-if="formatRemaining(license.expires_at)" class="subtle">
            （{{ formatRemaining(license.expires_at) }}）
          </small>
        </div>
      </div>

      <section class="sec">
        <h4>模組</h4>
        <div class="chips">
          <span
            v-for="m in modules"
            :key="m.key"
            class="chip"
            :class="{ 'has-addon': addonOf('modules', m.key) }"
            :title="addonOf('modules', m.key) && license ? moduleAddonHint(license, m.key) : undefined"
          >
            {{ m.text }}<small v-if="addonOf('modules', m.key)" class="ad-tag">＋加購</small>
          </span>
          <span v-if="!modules.length" class="subtle">—</span>
        </div>
      </section>

      <!-- 批114：整區改用共用元件（詳情類畫面一律列**全部**配額鍵，不收摘要）。
           加購標記與「已達上限」tooltip 的邏輯都在 `QuotaChips` 裡，代理商 realm 共用。 -->
      <section class="sec">
        <h4>額度</h4>
        <!-- ★ `usage` 直接傳整張證：`usageFrom` 會自己判形狀——我方 realm 的用量在
             `license.usage` 子物件裡，代理商 realm 的投影則是**扁平**的 `*_in_use` 欄
             （`ResellerLicense`）。寫死讀 `license.usage` 的話，代理商永遠看不到用量。 -->
        <QuotaChips
          :spec="spec"
          :quotas="effectiveQuotas"
          :usage="license"
          :license="license"
        />
      </section>

      <section v-if="policyRows.length" class="sec">
        <h4>行為參數</h4>
        <div class="chips">
          <span v-for="p in policyRows" :key="p.key" class="chip" :class="{ custom: p.custom }">
            {{ p.label }} {{ p.text }}
          </span>
        </div>
      </section>

      <section class="sec">
        <h4>
          加購子憑證
          <small v-if="addons.length" class="subtle">（{{ addons.length }}）</small>
        </h4>
        <div v-if="addonsBusy" class="subtle">載入中…</div>
        <div v-else-if="!addons.length" class="subtle">這台主機目前沒有加購。</div>
        <div v-else class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table addon-tbl">
            <thead>
              <tr><th>加購</th><th>內容</th><th>狀態</th><th>到期</th><th>來源</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="a in addons" :key="a.addon_id">
                <td>
                  <strong>{{ a.label }}</strong>
                  <span v-if="a.trial" class="chip">試用</span>
                  <div><code class="mono" :title="a.addon_id">{{ a.addon_id.slice(0, 8) }}</code></div>
                </td>
                <td class="small">{{ addonSummary(a, spec) }}</td>
                <td><span class="status-pill" :class="a.status">{{ addonStatusLabel(a) }}</span></td>
                <td>
                  {{ fmt(a.expires_at) }}
                  <div v-if="formatRemaining(a.expires_at)" class="sub subtle">
                    {{ formatRemaining(a.expires_at) }}
                  </div>
                </td>
                <td class="small">
                  {{ addonSourceLabel(a.source) }}
                  <div v-if="a.issued_by" class="sub subtle">{{ a.issued_by }}</div>
                </td>
                <td class="acts-cell">
                  <div class="acts">
                    <UiButton size="sm" @click="emit('addon-save', a)">下載</UiButton>
                    <UiButton size="sm" @click="emit('addon-copy', a)">複製</UiButton>
                    <template v-if="a.status === 'active' && canManageAddons">
                      <UiButton size="sm" @click="emit('addon-renew', a)">續期</UiButton>
                      <UiButton variant="danger" size="sm" @click="emit('addon-revoke', a)">
                        撤銷
                      </UiButton>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="license.deployment_mode === 'airgapped'" class="offline">
          ⚠️ 這是<strong>離線（氣隙）</strong>站台——它不跑心跳，<strong>撤銷不會自動生效</strong>；
          加購也要用「下載」或「複製」人工帶進站台。
        </p>
      </section>
    </template>

    <template #foot>
      <UiButton @click="emit('close')">關閉</UiButton>
      <!-- 批113：未綁主機的證換發必 422 `base_has_no_host`——與清單列同一套反灰＋title，
           而不是藏掉按鈕（藏掉會讓人以為這張證的詳情少了功能）。 -->
      <UiButton
        v-if="canReissue"
        variant="primary"
        :disabled="!license?.host_instance_id"
        :title="
          license?.host_instance_id
            ? undefined
            : '未綁主機的授權無法換發，請用〈客戶〉頁直接簽發'
        "
        @click="emit('reissue')"
      >換發新證</UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.head {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: var(--sp-3);
  border-bottom: 1px solid var(--border-2);
}
.plan {
  margin: 0 8px;
}
.term {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.sec {
  margin-top: var(--sp-4);
}
.sec h4 {
  margin: 0 0 6px;
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.chip.custom {
  border-color: var(--accent-500);
}
/* 批114：模組 chip 的加購標記（配額那半搬進 `QuotaChips` 後，這裡只剩模組用）。
   與 `QuotaChips.vue`／`LicensesView.vue` 同一套視覺，之前漏了樣式只有一個裸小字。 */
.chip.has-addon {
  border-color: var(--brand-600, var(--border-1));
}
.ad-tag {
  margin-left: 4px;
  color: var(--brand-600, var(--fg-3));
  font-size: var(--fs-10, 10px);
  font-weight: 600;
  cursor: help;
}
.offline {
  margin: var(--sp-2) 0 0;
  color: var(--warning);
  font-size: var(--fs-12);
}
</style>
